<template>
  <div class="validation-page-wrapper">
    <light-header class="validation-page-header" />

    <div v-if="isLoading" class="spinner-container flex--center--center">
      <app-spinner transparent-mode class="validation-page-spinner" />
    </div>
    <template v-else>
      <validation-page-template
        :patient-name="patientName"
        :patient-email="patientEmail"
        :doctor-full-name="doctorFullName"
        :doctor-website="doctorWebsite"
        :order-id="orderId"
        :delivery-days="deliveryDays"
        :is-renew="allowRenew"
        :is-serum-selected="isSerumSelected"
        :is-saudi-arabia="isSaudiArabia"
        :treatment-id="treatmentId"
        :payment-day="orderPaymentDate"
        :shipping-address="shippingAddressData"
        :billing-address="billingAddressData"
      >
        <template #renewSection>
          <renew-section :treatment-prototype-id="treatmentId" />
        </template>

        <template #introductionSection> </template>

        <template #orderSummary>
          <cart-footer-template
            should-display-total-cost
            can-display-delivery-cost
            :payment-info="paymentInfo"
            :should-show-v-a-t="shouldShowVAT"
            :custom-fee="customFeeValue"
            :delivery-cost="cartFooterDeliveryCost"
            :should-show-promo-code-section="shouldShowPromoCodeSection"
            :country="country"
          />
        </template>

        <template #orderInformation>
          <div>
            <cart-product
              v-for="(cartItem, i) of boughtCartItems"
              :key="i"
              :cart-item="cartItem"
              multiplier-update-disabled
              readonly
              show-total-price
            />
          </div>
        </template>
      </validation-page-template>
      <cart-checkout-footer class="validation-page-footer" />
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';

import ValidationPageTemplate from '@/components/validation-page/ValidationPageTemplate.vue';
import LightHeader from '@/components/common/app-header/LightHeader.vue';
import RenewSection from '@/components/validation-page/RenewSection.vue';
import CartFooterTemplate from '@/components/cart/CartFooterTemplate.vue';
import CartCheckoutFooter from '@/components/cart/CartCheckoutFooter.vue';
import CartProduct from '@/components/cart/CartProduct.vue';
import AppSpinner from '@/components/common/AppSpinner.vue';

import { sleep } from '@/utils/common';
import { formatDate } from '@/utils/date';

import { getValidationPageData } from '@/api/treatment.api';
import { getTransactionStatus } from '@/api/checkout.api';
import { getListOfBoughtCartItems } from '@/models/back-end/ValidationPageData';
import { isCartItemSerum } from '@/models/core/CartItem';
import { Price } from '@/models/core/values';

import { types } from '@/store/types';
import { TRANSACTION_STATUS } from '@/constants/payment';
import { COUNTRY_ISO } from '@/constants/countries';

const TRANSACTION_STATUS_CHECK_INITIAL_INTERVAL = 2000;
const TRANSACTION_STATUS_CHECK_INTERVAL_GROWTH_FACTOR = 1.2;
const SWIZZ_TAX_FREE_PRICE = 65;

const ROUTES_ALLOWED_TO_GO_FROM_VALIDATION_PAGE = [
  'WelcomePage',
  'CriticalErrorPage',
  'IntermediateRenewPage'
];
const ORDER_CREATION_DATE_FORMAT = 'd-MMM-y';
const PAYMENT_SYSTEMS_VALUE = {
  STRIPE: 'cart.cardLabel',
  FAKE: 'cart.freeLabel',
  PAYFORT: 'cart.freeLabel',
  PAYPAL: 'cart.paypalLabel'
};

const DELIVERY_TYPE_VALUES = {
  CLASSIC: 'checkout.delivery.classic.title',
  EXPRESS: 'checkout.delivery.express.title'
};

export default {
  name: 'ValidationPage',
  components: {
    ValidationPageTemplate,
    LightHeader,
    RenewSection,
    CartCheckoutFooter,
    CartFooterTemplate,
    CartProduct,
    AppSpinner
  },
  props: {
    allowRenew: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      patientName: '',
      patientEmail: '',
      orderId: null,
      doctorWebsite: '',
      deliveryDays: null,
      deliveryType: '',
      country: '',
      currency: '',
      customFee: '',
      subtotal: 0,
      promocodeType: '',
      promocode: null,
      vatAmountForWebshop: 0,
      handlingAndShipping: 0,
      paymentDate: '',
      paymentType: '',
      transactionStatusCheckInterval: TRANSACTION_STATUS_CHECK_INITIAL_INTERVAL,
      shippingAddress: {},
      billingAddress: {},
      boughtCartItems: []
    };
  },
  beforeRouteLeave(to, from, next) {
    // prevents user to navigate back in browser history seeing previous pages like
    // 3d-secure page or checkout page
    if (!ROUTES_ALLOWED_TO_GO_FROM_VALIDATION_PAGE.includes(to.name)) {
      next({
        name: 'WelcomePage',
        params: { doctorSlug: this.doctorSlug, hashedDataId: this.hashedDataId }
      });

      return;
    }

    next();
  },
  computed: {
    ...mapState({
      initialPageData: types.state.INITIAL_PAGE_DATA,
      doctorSlug: types.state.DOCTOR_SLUG,
      hashedDataId: types.state.HASHED_DATA_ID,
      locale: types.state.LOCALE,
      isLoading: types.state.IS_LOADING
    }),
    ...mapGetters({
      doctorFullName: types.getters.DOCTOR_FULL_NAME,
      treatmentId: types.getters.TREATMENT_ID
    }),
    shippingAddressData() {
      return {
        ...this.shippingAddress,
        paymentType: this.$t(PAYMENT_SYSTEMS_VALUE[this.paymentType]),
        deliveryType: this.$t(DELIVERY_TYPE_VALUES[this.deliveryType])
      };
    },
    billingAddressData() {
      return this.billingAddress
        ? { ...this.billingAddress, paymentType: this.paymentType }
        : this.shippingAddress;
    },
    shouldShowCustomsFeeWarning() {
      return this.shouldShowVAT || this.hasSwitzerlandDelivery;
    },
    shouldShowVAT() {
      return this.hasGreatBritainDelivery && !!this.vatAmountForWebshop;
    },
    hasSwitzerlandDelivery() {
      return this.country.toLowerCase() === COUNTRY_ISO.SWITZERLAND.toLowerCase();
    },
    hasGreatBritainDelivery() {
      return this.country.toLowerCase() === COUNTRY_ISO.GREAT_BRITAIN.toLowerCase();
    },
    isSaudiArabia() {
      return this.country.toLowerCase() === COUNTRY_ISO.SAUDI_ARABIA.toLowerCase();
    },
    cartFooterDeliveryCost() {
      if (!this.handlingAndShipping) {
        return Price(0, this.currency);
      }

      return Price(this.handlingAndShipping, this.currency);
    },
    orderPaymentDate() {
      if (!this.paymentDate) {
        return '';
      }

      const date = new Date(this.paymentDate);

      return formatDate(date, ORDER_CREATION_DATE_FORMAT);
    },
    hasSwitzerlandFreeCustomFee() {
      return this.hasSwitzerlandDelivery && this.orderTotalCost < SWIZZ_TAX_FREE_PRICE;
    },
    customFeeValue() {
      if (!this.shouldShowCustomsFeeWarning) {
        return null;
      }

      const value = this.hasSwitzerlandFreeCustomFee ? 0 : this.customFee;

      return Price(value, this.currency);
    },
    orderTotalCost() {
      return this.getTotalOrderCost();
    },
    shouldShowPromoCodeSection() {
      return !!this.promocode;
    },
    isSerumSelected() {
      return this.boughtCartItems.some(isCartItemSerum);
    },
    paymentInfo() {
      return {
        orderVAT: this.hasSwitzerlandFreeCustomFee
          ? Price(0, this.currency)
          : Price(this.vatAmountForWebshop, this.currency),
        subtotal: Price(this.subtotal, this.currency),
        totalCost: Price(this.orderTotalCost, this.currency)
      };
    }
  },
  async created() {
    this.updateIsLoadingStatus(true);

    const { success } = await this.checkTransactionStatus();

    if (!success) {
      return;
    }

    if (!this.treatmentId) {
      return;
    }

    const { data: validationPageData } = await getValidationPageData(this.treatmentId);
    this.updateIsLoadingStatus(false);

    const {
      orderId,
      doctorPage,
      deliveryDays,
      deliveryType,
      patientEmail,
      patientFirstName,
      billingAddress,
      shippingAddress,
      handlingAndShipping,
      country,
      currency,
      customFee,
      vatAmountForWebshop,
      promocode,
      promocodeType,
      paymentSystemType,
      subtotal,
      paymentDate
    } = validationPageData;

    this.orderId = orderId;
    this.doctorWebsite = doctorPage;
    this.deliveryDays = deliveryDays;
    this.patientEmail = patientEmail;
    this.patientName = patientFirstName;
    this.billingAddress = billingAddress;
    this.shippingAddress = shippingAddress;
    this.handlingAndShipping = Math.ceil(handlingAndShipping);
    this.vatAmountForWebshop = vatAmountForWebshop;
    this.paymentDate = paymentDate;
    this.country = country;
    this.currency = currency;
    this.customFee = customFee;
    this.promocode = promocode;
    this.subtotal = subtotal;
    this.deliveryType = deliveryType;
    this.promocodeType = promocodeType;
    this.paymentType = paymentSystemType;
    this.boughtCartItems = getListOfBoughtCartItems(validationPageData);

    if (this.promocode) {
      this.updatePromoCodeData({ amount: promocode, amountType: promocodeType });
    }
  },
  methods: {
    ...mapMutations({
      updatePromoCodeData: types.mutations.UPDATE_PROMO_CODE_DATA,
      updateIsLoadingStatus: types.mutations.UPDATE_IS_LOADING_STATUS
    }),

    async checkTransactionStatus() {
      const {
        data: { status, message }
      } = await getTransactionStatus(this.treatmentId);

      if (status === TRANSACTION_STATUS.PROCESSING) {
        await sleep(this.transactionStatusCheckInterval);
        this.transactionStatusCheckInterval *= TRANSACTION_STATUS_CHECK_INTERVAL_GROWTH_FACTOR;

        return this.checkTransactionStatus();
      }

      if (status === TRANSACTION_STATUS.ERROR) {
        await this.$router.push({
          name: 'CriticalErrorPage',
          params: {
            doctorSlug: this.doctorSlug,
            hashedDataId: this.hashedDataId,
            message
          }
        });

        return { success: false };
      }

      return { success: true };
    },
    getTotalOrderCost() {
      const totalCost = this.subtotal + this.handlingAndShipping;
      const orderTotal = this.shouldShowVAT ? totalCost - this.vatAmountForWebshop : totalCost;

      if (this.promocode) {
        const promocodeValue =
          this.promocodeType === 'PERCENTAGE' ? (totalCost * this.promocode) / 100 : this.promocode;

        return orderTotal - promocodeValue;
      }

      return orderTotal;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '../assets/scss/spinner.scss';

.validation-page-wrapper {
  background-color: inherit;
}

.validation-page-spinner {
  width: 60px;
  height: 60px;
}

.validation-page-header {
  display: none;
}

@media (min-width: 960px) {
  .validation-page-header {
    display: block;
  }
}
</style>
